import * as React from 'react';

const FULL_COVERAGE = [...Array(255).fill(0), 1].join(' ');
const HAS_COVERAGE = [0, ...Array(255).fill(1)].join(' ');
const ALPHA_TO_COLOR = '0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 0 1';
const COLOR_TO_ALPHA = '0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 0';

// At very small widths, use the original antialiased edge to distribute the
// bevel between adjacent pixels. The blurred distance alone can disagree with
// Chrome's coarse coverage samples. Cardinal erosion is always one whole pixel;
// the surface normal converts authored width to a fractional coverage step.
export function SubpixelBevelCoverage({ size }: { readonly size: number }) {
  const mix = Math.min(1, (0.5 - size) / 0.25);
  return (
    <>
      <feColorMatrix
        in="SourceAlpha"
        values={ALPHA_TO_COLOR}
        result="sourceColor"
      />
      <feBlend
        in="inverseLocalAlpha"
        in2="sourceColor"
        mode="color-dodge"
        result="sourceCoverageColor"
      />
      <feColorMatrix
        in="sourceCoverageColor"
        values={COLOR_TO_ALPHA}
        result="sourceCoverage"
      />
      {[0, 90, 180, 270].map((azimuth) => (
        <feDiffuseLighting
          key={azimuth}
          in="normalizedCoverage"
          surfaceScale={10000}
          lightingColor="white"
          result={`normal${azimuth}`}
        >
          <feDistantLight azimuth={azimuth} elevation={0} />
        </feDiffuseLighting>
      ))}
      <feBlend in="normal0" in2="normal180" mode="lighten" result="normalX" />
      <feBlend in="normal90" in2="normal270" mode="lighten" result="normalY" />
      <feBlend
        in="normalX"
        in2="normalY"
        mode="lighten"
        result="normalExtent"
      />
      <feColorMatrix
        in="normalExtent"
        values="-1 0 0 0 1  0 -1 0 0 1  0 0 -1 0 1  0 0 0 0 1"
        result="inverseNormalExtent"
      />
      <feColorMatrix
        in="normalExtent"
        values={`0 0 0 0 ${size}  0 0 0 0 ${size}  0 0 0 0 ${size}  0 0 0 0 1`}
        result="widthColor"
      />
      <feBlend
        in="inverseNormalExtent"
        in2="widthColor"
        mode="color-dodge"
        result="coverageStepColor"
      />
      <feColorMatrix
        in="coverageStepColor"
        values={COLOR_TO_ALPHA}
        result="coverageStep"
      />
      <feMorphology
        in="sourceCoverage"
        operator="erode"
        radius="1 0"
        result="neighborX"
      />
      <feMorphology
        in="sourceCoverage"
        operator="erode"
        radius="0 1"
        result="neighborY"
      />
      <feComposite
        in="neighborY"
        in2="neighborX"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="neighborDifference"
      />
      <feComposite
        in="neighborY"
        in2="neighborDifference"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="neighborCoverage"
      />
      <feComposite
        in="sourceCoverage"
        in2="coverageStep"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="partialInset"
      />
      <feComposite
        in="sourceCoverage"
        in2="partialInset"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="partialBand"
      />
      <feComposite
        in="neighborCoverage"
        in2="coverageStep"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="neighborInset"
      />
      <feComposite
        in="neighborCoverage"
        in2="neighborInset"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="neighborBand"
      />
      <feComponentTransfer in="sourceCoverage" result="fullCoverage">
        <feFuncA type="discrete" tableValues={FULL_COVERAGE} />
      </feComponentTransfer>
      <feComposite
        in="neighborBand"
        in2="fullCoverage"
        operator="in"
        result="interiorBand"
      />
      <feComposite
        in="partialBand"
        in2="interiorBand"
        operator="arithmetic"
        k2={1}
        k3={-1}
        result="subpixelBand"
      />
      <feColorMatrix
        in="sourceCoverage"
        values="0 0 0 -1 1  0 0 0 -1 1  0 0 0 -1 1  0 0 0 0 1"
        result="inverseSourceCoverage"
      />
      <feColorMatrix
        in="subpixelBand"
        values={ALPHA_TO_COLOR}
        result="subpixelBandColor"
      />
      <feBlend
        in="inverseSourceCoverage"
        in2="subpixelBandColor"
        mode="color-dodge"
        result="subpixelRatio"
      />
      <feColorMatrix
        in="subpixelRatio"
        values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  1 0 0 0 0"
        result="subpixelEdgeCoverage"
      />
      {/* Local fill opacity can vary across a gradient. Only correct pixels
          already identified as part of the inner boundary by the distance mask. */}
      <feComponentTransfer in="distanceBevelCoverage" result="bevelBoundary">
        <feFuncA type="discrete" tableValues={HAS_COVERAGE} />
      </feComponentTransfer>
      <feComposite
        in="subpixelEdgeCoverage"
        in2="bevelBoundary"
        operator="in"
        result="subpixelCoverage"
      />
      <feComposite
        in="subpixelCoverage"
        in2="distanceBevelCoverage"
        operator="arithmetic"
        k2={mix}
        k3={1 - mix}
        result="bevelCoverage"
      />
    </>
  );
}
